import { Suspense, useCallback, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';

import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';

import { AutomationsRecommendation } from './automations';
import { Card } from './card';
import { SourcingRecommendation } from './sourcing';

type RecommendationKey = 'sourcing' | 'automations';

function RecommendationsContent() {
  const [counts, setCounts] = useState<Record<RecommendationKey, number>>(
    {} as Record<RecommendationKey, number>,
  );

  // Update the count for the given recommendation key, this is used to show the recommendation header and count if at least one of the recommendations has data
  const updateCount = useCallback((key: RecommendationKey, count: number) => {
    setCounts(prev => {
      if (prev[key] === count) return prev;
      return { ...prev, [key]: count };
    });
  }, []);

  const allLoaded = Object.keys(counts).length === 2;
  const atleastOneLoadedWithData = Object.values(counts).some(
    count => count > 0,
  );
  const recommendationCount = Object.values(counts).reduce(
    (sum, c) => sum + c,
    0,
  );

  // If all recommendations are loaded and the recommendation count is 0, don't show the recommendations. Show skeleton until all recommendations are loaded.
  if (allLoaded && recommendationCount === 0) return null;

  return (
    <div className="shadow-base flex flex-col items-start gap-4 self-stretch rounded-lg bg-white p-4">
      <div className="flex items-center justify-between self-stretch">
        {/* Show the recommendation header and count if at least one of the recommendations has data */}
        {atleastOneLoadedWithData ? (
          <div className="flex items-center gap-3">
            <Typography variant="body2">
              <FormattedMessage defaultMessage="Suggested actions for you" />
            </Typography>
            <div className="bg-fx-rose flex h-4 w-6 items-center justify-center rounded-full px-1">
              <Typography variant="small2" className="text-white">
                {recommendationCount}
              </Typography>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Skeleton className="h-5 w-44" />
            <Skeleton className="h-4 w-6 rounded-full" />
          </div>
        )}
      </div>

      {/* Recommendation components with individual suspense fallbacks, so that we can fetch the recommendations one by one */}
      <div className="flex items-start gap-4 self-stretch">
        <Suspense fallback={<Card.Skeleton />}>
          <SourcingRecommendation
            onCountChange={c => updateCount('sourcing', c)}
          />
        </Suspense>

        <Suspense fallback={<Card.Skeleton />}>
          <AutomationsRecommendation
            onCountChange={c => updateCount('automations', c)}
          />
        </Suspense>
      </div>
    </div>
  );
}

export function Recommendations() {
  return (
    <ErrorBoundary fallback={null}>
      <RecommendationsContent />
    </ErrorBoundary>
  );
}
